import {Component, OnDestroy, OnInit} from '@angular/core';
import {Subscription} from 'rxjs';
import {
    XxPlaygroundQueryRun,
    XxSidecarPlaygroundService
} from '../../../service/xx-sidecar-playground.service';

/**
 * One render state of a single playground demo card.
 *
 * Each demo card (#4 External Flat / #5 External Typed / #3 Internal Typed)
 * keeps its own card state so the three demos run, load, error, and display
 * independently. `query`/`raw` are held verbatim so the page can show the exact
 * QL DTO that was sent and the raw backend response next to the mapped result.
 *
 * @ticket TM-1000001960
 */
interface XxPlaygroundCardState {
    /** True while this card's QL call is running. */
    loading: boolean;
    /** German error message for this card, or null when there is none. */
    errorMessage: string | null;
    /** True once this card has produced a result at least once. */
    executed: boolean;
    /** The exact QL query DTO that was sent (pretty-printed for the <pre>). */
    query: any | null;
    /** Dynamic table columns derived from the result rows. */
    columns: XxPlaygroundColumn[];
    /** The mapped result rows for the table. */
    rows: any[];
    /** The raw, unwrapped backend response (for the Raw-JSON panel). */
    raw: any | null;
}

/**
 * Dynamic table column descriptor. `key` is the property read off a result row;
 * `header` is the German display label shown in the table head.
 *
 * @ticket TM-1000001960
 */
interface XxPlaygroundColumn {
    /** Row property key (e.g. `firstName`, `ext_3`, `gen_7`). */
    key: string;
    /** Column header label. */
    header: string;
}

/**
 * Interactive QL-demo page for the Clean-Core External Side-Car pattern family
 * (TM-1000001960) — the xx-module "Side-Car Playground".
 *
 * The page is a teaching/inspection surface: the user picks an
 * ExternalPartyAssignment (and a XxPersonType for the typed demos), then runs
 * three independent demo cards. For each card the page shows:
 * - (a) the EXACT QL query DTO that was sent, as pretty JSON;
 * - (b) the result as a dynamic-column table (NULL → em dash);
 * - (c) a collapsible Raw-JSON panel with the unwrapped backend response.
 *
 * The three demos cover the full read-side of the side-car family (TM-1000001977:
 * the xx side-cars are version-axis scoped):
 * - Card 1 — #4 External Flat: bounded-external flatten with ONE join parameter
 *   (`external_party_version_id`) → `ext_<typeFieldId>` columns.
 * - Card 2 — #5 External Typed: bounded-external flatten with TWO join
 *   parameters (`external_party_version_id` + `type_id`) → `ext_<typeFieldId>`.
 * - Card 3 — #3 Internal Typed: read of `xx.XxPersonData`; generic-attribute
 *   values surface as `gen_<typeFieldId>` columns.
 *
 * All data flows through {@link XxSidecarPlaygroundService} — never a direct
 * HttpClient call. The mirrored PrimeNG component set (p-select, p-table,
 * p-panel, p-message, p-progressSpinner, p-button) matches the confirmed-working
 * xx-person-external-flat widget so the template AOT-compiles within XxModule.
 *
 * @ticket TM-1000001960
 */
@Component({
    selector: 'mvs-xx-sidecar-playground',
    templateUrl: './xx-sidecar-playground.component.html',
    styleUrls: ['./xx-sidecar-playground.component.scss'],
    standalone: false
})
export class XxSidecarPlaygroundComponent implements OnInit, OnDestroy {

    /** True once the selector lists have finished their first load. */
    initialized = false;

    /** True while the selector lists are loading. */
    selectorsLoading = false;

    /** German error message for the selector load, or null when there is none. */
    selectorError: string | null = null;

    /** ExternalPartyAssignment options for the party selector. */
    assignments: any[] = [];

    /** Currently selected ExternalPartyAssignment (drives all external demos). */
    selectedAssignment: any | null = null;

    /** XxPersonType options for the type selector (#3 / #5). */
    personTypes: any[] = [];

    /** Currently selected XxPersonType (drives #5 and filters #3). */
    selectedPersonType: any | null = null;

    /** Card state for Card 1 — #4 External Flat (one join parameter). */
    flatCard: XxPlaygroundCardState = this.emptyCard();

    /** Card state for Card 2 — #5 External Typed (two join parameters). */
    typedCard: XxPlaygroundCardState = this.emptyCard();

    /** Card state for Card 3 — #3 Internal Typed (generic attributes). */
    internalCard: XxPlaygroundCardState = this.emptyCard();

    /** Active subscriptions, torn down on destroy. */
    private subscriptions: Subscription[] = [];

    constructor(private playgroundService: XxSidecarPlaygroundService) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    /**
     * Initialize Component.
     *
     * @ticket TM-1000001960
     */
    initComponent(): void {
        this.refreshComponent();
    }

    /**
     * Refresh Component: (re)load the party and type selector lists.
     *
     * @ticket TM-1000001960
     */
    refreshComponent(): void {
        this.loadSelectors();
    }

    /**
     * Destroy component: unsubscribe from all running QL subscriptions.
     *
     * @ticket TM-1000001960
     */
    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.subscriptions = [];
    }

    /**
     * Load the ExternalPartyAssignment and XxPersonType selector lists.
     *
     * @ticket TM-1000001960
     */
    loadSelectors(): void {
        this.selectorsLoading = true;
        this.selectorError = null;

        const assignmentsSub = this.playgroundService.loadExternalPartyAssignments().subscribe({
            next: (assignments) => {
                this.assignments = assignments ?? [];
                this.loadPersonTypes();
            },
            error: () => {
                this.selectorsLoading = false;
                this.initialized = true;
                this.selectorError = 'Die externen Parteien / Zuordnungen konnten nicht geladen werden.';
            }
        });
        this.subscriptions.push(assignmentsSub);
    }

    /**
     * Load the XxPersonType selector list (second selector load step).
     *
     * @ticket TM-1000001960
     */
    private loadPersonTypes(): void {
        const typesSub = this.playgroundService.loadXxPersonTypes().subscribe({
            next: (types) => {
                this.personTypes = types ?? [];
                this.selectorsLoading = false;
                this.initialized = true;
            },
            error: () => {
                this.selectorsLoading = false;
                this.initialized = true;
                this.selectorError = 'Die Personentypen konnten nicht geladen werden.';
            }
        });
        this.subscriptions.push(typesSub);
    }

    // -------------------------------------------------------------------------
    // Card 1 — #4 External Flat (one join parameter)
    // -------------------------------------------------------------------------

    /**
     * Run the #4 External-Flat demo: discover the flat type-field ids for the
     * selected assignment's version stream, build the one-parameter bounded-external
     * flatten query, run it, then map `p.firstName`/`p.lastName`/`p.ext_<id>` to columns.
     *
     * TM-1000001977: discovery + read scope by `externalPartyVersionDtoId`, not the assignment.
     *
     * @ticket TM-1000001960
     * @ticket TM-1000001977
     */
    runFlatDemo(): void {
        if (!this.selectedAssignment) {
            this.flatCard.errorMessage = 'Bitte zuerst eine externe Partei / Zuordnung auswählen.';
            return;
        }

        const versionId = this.selectedAssignment.externalPartyVersionDtoId as number | undefined;
        if (versionId == null) {
            this.flatCard.errorMessage = 'Für diese Zuordnung ist kein Versionsstrom (ExternalPartyVersion) hinterlegt.';
            return;
        }
        this.startCard(this.flatCard);

        const sub = this.playgroundService.loadFlatTypeFieldIds(versionId).subscribe({
            next: (typeFieldIds) => {
                const ids = typeFieldIds ?? [];
                const query = this.playgroundService.buildExternalFlatQuery(versionId, ids);
                this.runExternalCard(this.flatCard, query, 'externalFlat', ids);
            },
            error: () => {
                this.flatCard.loading = false;
                this.flatCard.errorMessage = 'Die externen Felddefinitionen (Flat) konnten nicht geladen werden.';
            }
        });
        this.subscriptions.push(sub);
    }

    // -------------------------------------------------------------------------
    // Card 2 — #5 External Typed (two join parameters)
    // -------------------------------------------------------------------------

    /**
     * Run the #5 External-Typed demo: discover the typed type-field ids for the
     * selected (version, type) pair, build the two-parameter bounded-external
     * flatten query, run it, then map the same flat columns.
     *
     * TM-1000001977: discovery + read scope by `externalPartyVersionDtoId` + `type_id`,
     * not the assignment.
     *
     * @ticket TM-1000001960
     * @ticket TM-1000001977
     */
    runTypedDemo(): void {
        if (!this.selectedAssignment) {
            this.typedCard.errorMessage = 'Bitte zuerst eine externe Partei / Zuordnung auswählen.';
            return;
        }
        if (!this.selectedPersonType) {
            this.typedCard.errorMessage = 'Bitte zuerst einen Personentyp auswählen.';
            return;
        }

        const versionId = this.selectedAssignment.externalPartyVersionDtoId as number | undefined;
        if (versionId == null) {
            this.typedCard.errorMessage = 'Für diese Zuordnung ist kein Versionsstrom (ExternalPartyVersion) hinterlegt.';
            return;
        }
        const typeId = this.selectedPersonType.id as number;
        this.startCard(this.typedCard);

        const sub = this.playgroundService.loadTypedTypeFieldIds(versionId, typeId).subscribe({
            next: (typeFieldIds) => {
                const ids = typeFieldIds ?? [];
                const query = this.playgroundService.buildExternalTypedQuery(versionId, typeId, ids);
                this.runExternalCard(this.typedCard, query, 'externalTyped', ids);
            },
            error: () => {
                this.typedCard.loading = false;
                this.typedCard.errorMessage = 'Die externen Felddefinitionen (Typed) konnten nicht geladen werden.';
            }
        });
        this.subscriptions.push(sub);
    }

    // -------------------------------------------------------------------------
    // Card 3 — #3 Internal Typed (generic attributes)
    // -------------------------------------------------------------------------

    /**
     * Run the #3 Internal-Typed demo: read `xx.XxPersonData` (optionally filtered
     * by the selected XxPersonType). Columns are derived from the row keys so the
     * dynamic `gen_<typeFieldId>` generic-attribute fields appear automatically.
     *
     * @ticket TM-1000001960
     */
    runInternalDemo(): void {
        const typeId = this.selectedPersonType ? (this.selectedPersonType.id as number) : undefined;
        this.startCard(this.internalCard);

        // The internal read uses loadPersonData which builds the query internally;
        // mirror that exact DTO for display so the user sees the QL that was sent.
        this.internalCard.query = this.buildInternalQueryForDisplay(typeId);

        const sub = this.playgroundService.loadPersonData(typeId).subscribe({
            next: (rows) => {
                const resultRows = rows ?? [];
                this.internalCard.rows = resultRows;
                this.internalCard.columns = this.buildColumnsFromRows(resultRows);
                this.internalCard.raw = resultRows;
                this.internalCard.loading = false;
            },
            error: () => {
                this.internalCard.loading = false;
                this.internalCard.errorMessage = 'Die internen Personendaten (Internal Typed) konnten nicht geladen werden.';
            }
        });
        this.subscriptions.push(sub);
    }

    // -------------------------------------------------------------------------
    // Shared run / mapping helpers
    // -------------------------------------------------------------------------

    /**
     * Execute an external (bounded-external flatten) query for a card and map the
     * `simple`-pipe rows (`p.firstName`/`p.lastName`/`p.ext_<id>`) into flat
     * columns. Shared by Card 1 (#4) and Card 2 (#5).
     *
     * @ticket TM-1000001960
     */
    private runExternalCard(card: XxPlaygroundCardState, query: any, resultKey: string, typeFieldIds: number[]): void {
        card.query = query;

        const sub = this.playgroundService.runQuery(query, resultKey, 'simple').subscribe({
            next: (run: XxPlaygroundQueryRun) => {
                card.columns = this.buildExternalColumns(typeFieldIds);
                card.rows = this.mapExternalRows(run.rows, typeFieldIds);
                card.raw = run.raw;
                card.loading = false;
            },
            error: () => {
                card.loading = false;
                card.errorMessage = 'Die QL-Abfrage konnte nicht ausgeführt werden.';
            }
        });
        this.subscriptions.push(sub);
    }

    /**
     * Build the dynamic columns for an external flatten result: the two core
     * person columns plus one `ext_<id>` column per requested type-field id.
     *
     * @ticket TM-1000001960
     */
    private buildExternalColumns(typeFieldIds: number[]): XxPlaygroundColumn[] {
        const columns: XxPlaygroundColumn[] = [
            {key: 'firstName', header: 'Vorname'},
            {key: 'lastName', header: 'Nachname'}
        ];
        for (const id of typeFieldIds) {
            columns.push({key: `ext_${id}`, header: `ext_${id}`});
        }
        return columns;
    }

    /**
     * Normalize a `simple`-pipe bounded-external result into flat display rows:
     * `p.firstName`/`p.lastName` → top-level, `p.ext_<id>` → `ext_<id>`.
     *
     * @ticket TM-1000001960
     */
    private mapExternalRows(rows: any[], typeFieldIds: number[]): any[] {
        return (rows ?? []).map((row: any) => {
            const flat: any = {
                firstName: row['p.firstName'],
                lastName: row['p.lastName']
            };
            for (const id of typeFieldIds) {
                flat[`ext_${id}`] = row[`p.ext_${id}`];
            }
            return flat;
        });
    }

    /**
     * Mirror the QL DTO that {@link XxSidecarPlaygroundService#loadPersonData}
     * builds internally, so the #3 card can display the exact query that was
     * sent. Kept structurally identical to the service implementation.
     *
     * @ticket TM-1000001960
     */
    private buildInternalQueryForDisplay(typeId?: number): any {
        const query: any = {
            name: 'personData',
            type: 'view',
            start: {type: 'entity', name: 'xx.XxPersonData', as: 'e'}
        };
        if (typeId != null) {
            query.filters = [
                {field: 'e.typeId', operator: 'EQUAL', value: typeId}
            ];
        }
        return query;
    }

    /**
     * Derive dynamic columns from the union of keys across all result rows. Used
     * by Card 3 so the generic-attribute `gen_<typeFieldId>` fields render as
     * columns without any fixed schema. Column order follows first-seen order.
     *
     * @ticket TM-1000001960
     */
    private buildColumnsFromRows(rows: any[]): XxPlaygroundColumn[] {
        const seen = new Set<string>();
        const columns: XxPlaygroundColumn[] = [];
        for (const row of rows ?? []) {
            if (!row || typeof row !== 'object') {
                continue;
            }
            for (const key of Object.keys(row)) {
                if (!seen.has(key)) {
                    seen.add(key);
                    columns.push({key, header: key});
                }
            }
        }
        return columns;
    }

    /**
     * Reset a card to its loading state before a fresh run.
     *
     * @ticket TM-1000001960
     */
    private startCard(card: XxPlaygroundCardState): void {
        card.loading = true;
        card.executed = true;
        card.errorMessage = null;
        card.query = null;
        card.columns = [];
        card.rows = [];
        card.raw = null;
    }

    /**
     * Build an empty card state for field initialization.
     *
     * @ticket TM-1000001960
     */
    private emptyCard(): XxPlaygroundCardState {
        return {
            loading: false,
            errorMessage: null,
            executed: false,
            query: null,
            columns: [],
            rows: [],
            raw: null
        };
    }

    // -------------------------------------------------------------------------
    // Display helpers (template-facing)
    // -------------------------------------------------------------------------

    /**
     * Human-readable label for an ExternalPartyAssignment: prefer the assignment
     * name, then the linked ExternalParty name, finally fall back to the id.
     *
     * @ticket TM-1000001960
     */
    resolveAssignmentLabel(assignment: any): string {
        if (!assignment) {
            return '';
        }
        if (assignment.name) {
            return assignment.name;
        }
        if (assignment.externalPartyDtoName) {
            return assignment.externalPartyDtoName;
        }
        return `Zuordnung ${assignment.id}`;
    }

    /**
     * Human-readable label for a XxPersonType: prefer name, fall back to the id.
     *
     * @ticket TM-1000001960
     */
    resolvePersonTypeLabel(type: any): string {
        if (!type) {
            return '';
        }
        return type.name ?? `Typ ${type.id}`;
    }

    /**
     * Pretty-print a QL query DTO for the `<pre>` display.
     *
     * @ticket TM-1000001960
     */
    formatQuery(query: any): string {
        if (query == null) {
            return '';
        }
        return JSON.stringify(query, null, 2);
    }

    /**
     * Pretty-print the raw backend response for the Raw-JSON panel.
     *
     * @ticket TM-1000001960
     */
    formatRaw(raw: any): string {
        if (raw == null) {
            return '—';
        }
        return JSON.stringify(raw, null, 2);
    }

    /**
     * Render a result-row cell value. NULL / undefined / empty values render as
     * an em dash so absent fields are visible. Objects are JSON-stringified.
     *
     * @ticket TM-1000001960
     */
    displayValue(row: any, key: string): string {
        const value = row ? row[key] : undefined;
        if (value === null || value === undefined || value === '') {
            return '—';
        }
        if (typeof value === 'object') {
            return JSON.stringify(value);
        }
        return String(value);
    }
}
